import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset for testing data-table control with an exponential function.
 * Shows a table of (x, f(x)) values alongside the graph.
 * Hovering a row reveals the corresponding point on the curve.
 */
export const TEST_DATA_TABLE_PRESET: GraphpadWidgetConfig = {
  id: 'test-data-table',
  type: 'graphpad',
  params: {
    f: {
      type: 'exponential',
      params: { a: 1, b: 2, h: 0, k: 0 },
    },
    // Hidden points revealed on table row hover
    'p-neg2': { type: 'point', params: { x: -2, y: 0.25 } },
    'p-neg1': { type: 'point', params: { x: -1, y: 0.5 } },
    'p-0': { type: 'point', params: { x: 0, y: 1 } },
    'p-1': { type: 'point', params: { x: 1, y: 2 } },
    'p-2': { type: 'point', params: { x: 2, y: 4 } },
    'p-3': { type: 'point', params: { x: 3, y: 8 } },
  },
  display: {
    canvas: { color: 'neutral-0' },
    coordinateSystem: {
      boundingbox: { xStart: -4, xEnd: 5, yStart: -1, yEnd: 10 },
      grid: { type: 'lines', visible: true },
    },
    elements: {
      'p-neg2': { visible: false, point: { color: 'sky', label: { contentVariant: 'coord' } } },
      'p-neg1': { visible: false, point: { color: 'sky', label: { contentVariant: 'coord' } } },
      'p-0': { visible: false, point: { color: 'sky', label: { contentVariant: 'coord' } } },
      'p-1': { visible: false, point: { color: 'sky', label: { contentVariant: 'coord' } } },
      'p-2': { visible: false, point: { color: 'sky', label: { contentVariant: 'coord' } } },
      'p-3': { visible: false, point: { color: 'sky', label: { contentVariant: 'coord' } } },
    },
  },
  behavior: {
    controls: [
      {
        id: 'data-table-f',
        type: WIDGET_CONTROL_TYPE.DataTable,
        label: '$f(x) = 2^x$',
        position: 'right',
        columns: [
          { key: 'x', header: '$x$', align: 'center' },
          { key: 'fx', header: '$f(x)$', align: 'center' },
        ],
        rows: [
          {
            values: { x: -2, fx: 0.25 },
            onHover: { action: 'PATCH_DISPLAY', payload: { 'p-neg2': { visible: true } } },
            onLeave: { action: 'PATCH_DISPLAY', payload: { 'p-neg2': { visible: false } } },
          },
          {
            values: { x: -1, fx: 0.5 },
            onHover: { action: 'PATCH_DISPLAY', payload: { 'p-neg1': { visible: true } } },
            onLeave: { action: 'PATCH_DISPLAY', payload: { 'p-neg1': { visible: false } } },
          },
          {
            values: { x: 0, fx: 1 },
            onHover: { action: 'PATCH_DISPLAY', payload: { 'p-0': { visible: true } } },
            onLeave: { action: 'PATCH_DISPLAY', payload: { 'p-0': { visible: false } } },
          },
          {
            values: { x: 1, fx: 2 },
            onHover: { action: 'PATCH_DISPLAY', payload: { 'p-1': { visible: true } } },
            onLeave: { action: 'PATCH_DISPLAY', payload: { 'p-1': { visible: false } } },
          },
          {
            values: { x: 2, fx: 4 },
            onHover: { action: 'PATCH_DISPLAY', payload: { 'p-2': { visible: true } } },
            onLeave: { action: 'PATCH_DISPLAY', payload: { 'p-2': { visible: false } } },
          },
          {
            values: { x: 3, fx: 8 },
            onHover: { action: 'PATCH_DISPLAY', payload: { 'p-3': { visible: true } } },
            onLeave: { action: 'PATCH_DISPLAY', payload: { 'p-3': { visible: false } } },
          },
        ],
      },
    ],
  },
};
